import { Button, Label, Spinner, TextInput } from 'flowbite-react';
import React from 'react';
import { IoMdAdd, IoMdRemove } from 'react-icons/io';
import { useMutation, useQueryClient } from 'react-query';
import { updateNoOfRounds } from 'src/apis/application';
import { useToast } from 'src/context/ToastProvider';
import { ShortListCandidateInput } from 'types';

interface Props {
	userId?: string;
	userName?: string;
	currentRound: number;
	totalRounds: number;
	positionId: string;
	onSubmit?: (data: ShortListCandidateInput) => void;
	onFinish?: () => void;
	nextRoundLabel?: string;
}

const RecruiterCandidateUpdateRound = ({
	userId,
	userName,
	onSubmit,
	onFinish,
	positionId,
	currentRound,
	totalRounds,
	nextRoundLabel,
}: Props) => {
	const limit = 10;
	const toast = useToast();
	const queryClient = useQueryClient();
	const [round, setRound] = React.useState<number>(totalRounds);

	React.useEffect(() => {
		setRound(totalRounds);

		return () => {
			setRound(0);
		};
	}, [totalRounds]);

	const updateNoOfRoundsFn = useMutation(['updateNoOfRounds'], updateNoOfRounds, {
		onSuccess(data) {
			queryClient.refetchQueries({ queryKey: ['getRecommendationsForPosition'], exact: false });
			toast({
				title: `No of rounds updated successfully`,
				type: 'success',
			});
			onFinish && onFinish();
		},
		onError(error: Error) {
			toast({
				title: `Error occured ${error.message}`,
				type: 'error',
			});
		},
	});

	const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		if (onSubmit) onSubmit({ positionId, userId: userId ?? '' });
		else
			updateNoOfRoundsFn.mutate({
				positionId,
				applicationId: '',
				userId: userId ?? '',
				updatedNumberOfRounds: round,
			});
	};

	return (
		<form onSubmit={handleSubmit}>
			<div>
				<div className="mb-[8px]">
					<Label>{'Total rounds '}</Label>
					<span className="text-[red]">*</span>
				</div>

				<div className="flex items-center gap-[8px]">
					<TextInput
						type="number"
						className="w-full mr-[7px]"
						value={round}
						onChange={e => {
							if (+e.target.value >= currentRound && +e.target.value <= limit) {
								setRound(+e.target.value);
							}
						}}
					/>
					<Button
						className="text-[#1C1C28] rounded-[8px] p-[8px] [&>span]:!p-0"
						color="light"
						disabled={currentRound > round - 1}
						onClick={() => {
							if (currentRound <= round - 1) {
								setRound(prev => prev - 1);
							}
						}}
					>
						<IoMdRemove className="w-[24px] h-[24px]" />
					</Button>
					<Button
						className="text-[#1C1C28] rounded-[8px] p-[8px] [&>span]:!p-0"
						color="light"
						disabled={round === limit}
						onClick={() => {
							if (round <= limit) setRound(prev => prev + 1);
						}}
					>
						<IoMdAdd className="w-[24px] h-[24px]" />
					</Button>
				</div>
			</div>

			<Button
				type="submit"
				className="w-full mt-[20px] bg-[#7777E7]"
				color="purplePlaced"
				disabled={!round || updateNoOfRoundsFn?.isLoading}
			>
				{updateNoOfRoundsFn.isLoading ? <Spinner /> : 'Update'}
			</Button>
		</form>
	);
};

export default RecruiterCandidateUpdateRound;
